/**
 * Curated catalog of city neighborhoods with a reputation for being
 * genuinely pleasant to walk through — tree-lined streets, brownstones,
 * pedestrian malls, historic blocks, café culture, low traffic.
 *
 * Used as a WALKING-mode scoring boost: when a meaningful share of a
 * walking route passes through one of these neighborhoods, that route
 * climbs the ranking over an equally-direct alternative on dull commercial
 * arterials. NOT used for driving (highway routing dwarfs neighborhood
 * texture) or biking (bike-lane infrastructure is the dominant signal).
 *
 * Each entry is a point + radius in meters that loosely covers the
 * neighborhood footprint. Keep entries conservative — false positives
 * (e.g. extending a "West Village" radius into Hudson Square office land)
 * pollute the boost on routes that don't actually feel pretty.
 */

import type { LngLat } from "@/utils/polyline";
import { haversine } from "@/utils/polyline";

export interface PrettyNeighborhood {
  name: string;
  city: string;
  /** [lng, lat] of the neighborhood centroid. */
  coord: LngLat;
  /** Effective radius in meters. */
  radiusM: number;
  /** Charm tier — drives the boost magnitude. 3 = world-class (Marais,
   *  West Village), 2 = very pretty, 1 = pretty. */
  tier: 1 | 2 | 3;
}

export const PRETTY_NEIGHBORHOODS: PrettyNeighborhood[] = [
  // ───── New York City ─────
  { name: "West Village",         city: "NYC", coord: [-74.0045, 40.7345], radiusM:  650, tier: 3 },
  { name: "Greenwich Village",    city: "NYC", coord: [-73.9988, 40.7335], radiusM:  600, tier: 3 },
  { name: "SoHo",                 city: "NYC", coord: [-74.0006, 40.7237], radiusM:  500, tier: 3 },
  { name: "NoLita",               city: "NYC", coord: [-73.9952, 40.7223], radiusM:  350, tier: 2 },
  { name: "Tribeca",              city: "NYC", coord: [-74.0094, 40.7195], radiusM:  500, tier: 2 },
  { name: "East Village",         city: "NYC", coord: [-73.9836, 40.7264], radiusM:  500, tier: 2 },
  { name: "Upper East Side (Historic)", city: "NYC", coord: [-73.9628, 40.7736], radiusM: 700, tier: 2 },
  { name: "Upper West Side",      city: "NYC", coord: [-73.9760, 40.7870], radiusM:  700, tier: 2 },
  { name: "Brooklyn Heights",     city: "NYC", coord: [-73.9942, 40.6961], radiusM:  500, tier: 3 },
  { name: "Cobble Hill",          city: "NYC", coord: [-73.9963, 40.6862], radiusM:  400, tier: 2 },
  { name: "Carroll Gardens",      city: "NYC", coord: [-73.9989, 40.6798], radiusM:  450, tier: 2 },
  { name: "Park Slope",           city: "NYC", coord: [-73.9779, 40.6722], radiusM:  700, tier: 2 },
  { name: "DUMBO",                city: "NYC", coord: [-73.9893, 40.7033], radiusM:  350, tier: 2 },
  { name: "Williamsburg waterfront", city: "NYC", coord: [-73.9619, 40.7211], radiusM: 500, tier: 2 },
  { name: "Fort Greene",          city: "NYC", coord: [-73.9745, 40.6900], radiusM:  450, tier: 2 },
  { name: "Chelsea (High Line)",  city: "NYC", coord: [-74.0048, 40.7480], radiusM:  500, tier: 2 },

  // ───── Boston ─────
  { name: "Beacon Hill",          city: "Boston", coord: [-71.0654, 42.3588], radiusM: 400, tier: 3 },
  { name: "North End",            city: "Boston", coord: [-71.0540, 42.3647], radiusM: 350, tier: 3 },
  { name: "Back Bay",             city: "Boston", coord: [-71.0810, 42.3500], radiusM: 600, tier: 2 },
  { name: "South End",            city: "Boston", coord: [-71.0732, 42.3402], radiusM: 600, tier: 2 },
  { name: "Charlestown",          city: "Boston", coord: [-71.0617, 42.3782], radiusM: 450, tier: 2 },

  // ───── San Francisco ─────
  { name: "North Beach",          city: "SF", coord: [-122.4093, 37.8003], radiusM: 400, tier: 3 },
  { name: "Russian Hill",         city: "SF", coord: [-122.4180, 37.8013], radiusM: 400, tier: 3 },
  { name: "Pacific Heights",      city: "SF", coord: [-122.4380, 37.7935], radiusM: 600, tier: 2 },
  { name: "Hayes Valley",         city: "SF", coord: [-122.4248, 37.7762], radiusM: 350, tier: 2 },
  { name: "Mission Dolores",      city: "SF", coord: [-122.4260, 37.7600], radiusM: 400, tier: 2 },
  { name: "Castro",               city: "SF", coord: [-122.4346, 37.7609], radiusM: 350, tier: 2 },
  { name: "Cole Valley",          city: "SF", coord: [-122.4500, 37.7660], radiusM: 350, tier: 2 },

  // ───── Chicago ─────
  { name: "Old Town",             city: "Chicago", coord: [-87.6356, 41.9118], radiusM: 400, tier: 2 },
  { name: "Lincoln Park",         city: "Chicago", coord: [-87.6489, 41.9214], radiusM: 600, tier: 2 },
  { name: "Wicker Park",          city: "Chicago", coord: [-87.6770, 41.9088], radiusM: 500, tier: 2 },
  { name: "Gold Coast",           city: "Chicago", coord: [-87.6262, 41.9044], radiusM: 450, tier: 2 },

  // ───── Washington DC ─────
  { name: "Georgetown",           city: "DC", coord: [-77.0636, 38.9097], radiusM: 600, tier: 3 },
  { name: "Capitol Hill",         city: "DC", coord: [-77.0010, 38.8869], radiusM: 600, tier: 2 },
  { name: "Dupont Circle",        city: "DC", coord: [-77.0434, 38.9097], radiusM: 450, tier: 2 },

  // ───── New Orleans / Charleston / Savannah ─────
  { name: "French Quarter",       city: "New Orleans", coord: [-90.0640, 29.9584], radiusM: 500, tier: 3 },
  { name: "Garden District",      city: "New Orleans", coord: [-90.0838, 29.9276], radiusM: 500, tier: 3 },
  { name: "Historic Charleston",  city: "Charleston",  coord: [-79.9320, 32.7770], radiusM: 700, tier: 3 },
  { name: "Savannah Historic District", city: "Savannah", coord: [-81.0930, 32.0750], radiusM: 700, tier: 3 },

  // ───── Seattle / Portland ─────
  { name: "Capitol Hill",         city: "Seattle", coord: [-122.3170, 47.6210], radiusM: 500, tier: 2 },
  { name: "Ballard",              city: "Seattle", coord: [-122.3839, 47.6685], radiusM: 500, tier: 2 },
  { name: "Pearl District",       city: "Portland", coord: [-122.6830, 45.5290], radiusM: 500, tier: 2 },

  // ───── Paris ─────
  { name: "Le Marais",            city: "Paris", coord: [2.3590, 48.8580], radiusM: 600, tier: 3 },
  { name: "Saint-Germain-des-Prés", city: "Paris", coord: [2.3340, 48.8540], radiusM: 500, tier: 3 },
  { name: "Île Saint-Louis",      city: "Paris", coord: [2.3570, 48.8517], radiusM: 250, tier: 3 },
  { name: "Montmartre",           city: "Paris", coord: [2.3414, 48.8867], radiusM: 500, tier: 3 },
  { name: "Canal Saint-Martin",   city: "Paris", coord: [2.3650, 48.8730], radiusM: 400, tier: 2 },
  { name: "Latin Quarter",        city: "Paris", coord: [2.3470, 48.8510], radiusM: 500, tier: 2 },

  // ───── London ─────
  { name: "Notting Hill",         city: "London", coord: [-0.2050, 51.5150], radiusM: 600, tier: 3 },
  { name: "Hampstead",            city: "London", coord: [-0.1780, 51.5560], radiusM: 600, tier: 3 },
  { name: "Marylebone",           city: "London", coord: [-0.1520, 51.5180], radiusM: 500, tier: 2 },
  { name: "Covent Garden",        city: "London", coord: [-0.1240, 51.5120], radiusM: 350, tier: 2 },
  { name: "Chelsea",              city: "London", coord: [-0.1690, 51.4870], radiusM: 600, tier: 2 },
  { name: "Greenwich",            city: "London", coord: [-0.0090, 51.4810], radiusM: 500, tier: 2 },

  // ───── Other Europe ─────
  { name: "Trastevere",           city: "Rome", coord: [12.4690, 41.8890], radiusM: 500, tier: 3 },
  { name: "Centro Storico",       city: "Rome", coord: [12.4760, 41.8990], radiusM: 600, tier: 3 },
  { name: "Gràcia",               city: "Barcelona", coord: [2.1560, 41.4030], radiusM: 500, tier: 2 },
  { name: "Gothic Quarter",       city: "Barcelona", coord: [2.1770, 41.3830], radiusM: 400, tier: 3 },
  { name: "El Born",              city: "Barcelona", coord: [2.1830, 41.3850], radiusM: 350, tier: 2 },
  { name: "Jordaan",              city: "Amsterdam", coord: [4.8830, 52.3740], radiusM: 500, tier: 3 },
  { name: "De Pijp",              city: "Amsterdam", coord: [4.8930, 52.3550], radiusM: 450, tier: 2 },
  { name: "Kreuzberg",            city: "Berlin", coord: [13.4180, 52.4990], radiusM: 600, tier: 2 },
  { name: "Prenzlauer Berg",      city: "Berlin", coord: [13.4170, 52.5380], radiusM: 600, tier: 2 },
  { name: "Alfama",               city: "Lisbon", coord: [-9.1290, 38.7110], radiusM: 400, tier: 3 },
  { name: "Bairro Alto",          city: "Lisbon", coord: [-9.1450, 38.7130], radiusM: 350, tier: 2 },
];

/** For each polyline vertex, check membership in any pretty neighborhood.
 *  Returns the FRACTION of route length (0..1) that falls inside one,
 *  weighted by tier so a tier-3 stretch counts more than tier-1.
 *
 *  `extra` lets callers add user-nominated neighborhoods on top of the
 *  hardcoded catalog so community submissions influence scoring. */
export function prettyNeighborhoodCoverage(
  coords: LngLat[],
  extra: PrettyNeighborhood[] = [],
): { fraction: number; weighted: number; names: string[] } {
  if (coords.length < 2) return { fraction: 0, weighted: 0, names: [] };

  // Quick reject: only consider neighborhoods within ~5km of the bbox
  // center. Avoids 60+ haversine checks per vertex on a long route.
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const center: LngLat = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  const halfDiag = haversine([minLng, minLat], [maxLng, maxLat]) / 2;
  const pool = [...PRETTY_NEIGHBORHOODS, ...extra];
  const candidates = pool.filter(
    (n) => haversine(center, n.coord) < halfDiag + n.radiusM + 5000,
  );
  if (candidates.length === 0) return { fraction: 0, weighted: 0, names: [] };

  let totalM = 0;
  let insideM = 0;
  let weightedM = 0;
  const namesHit = new Set<string>();
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1];
    const b = coords[i];
    const segLen = haversine(a, b);
    totalM += segLen;
    // Use the midpoint to classify the segment — cheap and accurate enough.
    const mid: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    let bestTier = 0;
    let bestName: string | undefined;
    for (const n of candidates) {
      if (haversine(mid, n.coord) <= n.radiusM) {
        if (n.tier > bestTier) {
          bestTier = n.tier;
          bestName = n.name;
        }
      }
    }
    if (bestTier > 0) {
      insideM += segLen;
      weightedM += segLen * bestTier;
      if (bestName) namesHit.add(bestName);
    }
  }
  if (totalM === 0) return { fraction: 0, weighted: 0, names: [] };
  return {
    fraction: insideM / totalM,
    // Normalize weighted by the max possible (tier 3 everywhere = 3x).
    weighted: weightedM / (totalM * 3),
    names: Array.from(namesHit),
  };
}
